import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Link,
  Paper,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LandscapeIcon from '@mui/icons-material/Landscape';
import ChooseCycleDialog from '../components/shapeup/ChooseCycleDialog';
import PitchCard from '../components/shapeup/PitchCard';
import PitchDialog from '../components/shapeup/PitchDialog';
import { EntityCommentsProvider } from '../components/shapeup/useEntityComments';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  betOnPitch,
  createPitch,
  deletePitch,
  getCycles,
  getPitches,
  updatePitch,
} from '../utils/shapeUpApi';

const TABS = [
  { value: 'ready', label: 'On the table' },
  { value: 'shaping', label: 'Still shaping' },
  { value: 'bet', label: 'Bet on' },
  { value: 'archived', label: 'Archived' },
];

const EMPTY_COPY = {
  ready: {
    title: 'Nothing on the table',
    body: 'A pitch belongs here once it is shaped: a problem, an appetite, a rough solution, '
      + 'and the rabbit holes and no-gos that keep a cycle inside its budget.',
  },
  shaping: {
    title: 'Nothing being shaped',
    body: 'Shaping happens before the betting table. Write the problem down while it is still '
      + 'rough and work out what you would deliberately leave out.',
  },
  bet: {
    title: 'Nothing bet on yet',
    body: 'Pitches move here when they win a bet, so you can see what was chosen and what it '
      + 'was chosen over.',
  },
  archived: {
    title: 'Nothing archived',
    body: 'Pitches that went stale or stopped mattering end up here.',
  },
};

/**
 * The betting table: shaped work waiting for a decision.
 *
 * Deliberately not a backlog. Pitches sit here as candidates and go stale if
 * nobody bets on them, which is the mechanism that stops a list of ideas from
 * quietly becoming a queue nobody ever finishes.
 */
const PitchesPage = () => {
  const { organizations, currentOrganization } = useWorkspace();

  const [organizationId, setOrganizationId] = useState('');
  const [pitches, setPitches] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [tab, setTab] = useState('ready');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogPitch, setDialogPitch] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bettingPitch, setBettingPitch] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (currentOrganization?.id) setOrganizationId(currentOrganization.id);
    else if (organizations.length > 0) setOrganizationId(organizations[0].id);
    else setLoading(false);
  }, [currentOrganization, organizations]);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const [pitchData, cycleData] = await Promise.all([
        getPitches({ organizationId }),
        getCycles({ organizationId }).catch(() => ({ cycles: [] })),
      ]);
      setPitches(pitchData.pitches || []);
      setCycles(cycleData.cycles || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load pitches.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (form) => {
    if (dialogPitch) {
      await updatePitch(dialogPitch.id, form);
      setToast({ severity: 'success', message: `Updated ${form.title}` });
    } else {
      await createPitch({ organizationId, ...form });
      setToast({ severity: 'success', message: `Added ${form.title}` });
    }
    await load();
  };

  const handleMarkReady = async (pitch) => {
    try {
      await updatePitch(pitch.id, { status: 'ready' });
      await load();
      setToast({ severity: 'success', message: `${pitch.title} is on the table` });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update the pitch.');
    }
  };

  const handleDelete = async (pitch) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the pitch "${pitch.title}"?`)) return;
    try {
      await deletePitch(pitch.id);
      await load();
      setToast({ severity: 'success', message: 'Pitch deleted' });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not delete the pitch.');
    }
  };

  const handleBet = async (cycleId) => {
    const result = await betOnPitch(bettingPitch.id, cycleId);
    await load();
    const cycle = cycles.find(c => c.id === cycleId);
    setToast({
      severity: 'success',
      message: `${result.bet.name} is a bet in ${cycle?.name || 'the cycle'}. Link it to a Jira epic to fill the hill.`,
    });
  };

  const shown = pitches.filter(pitch => pitch.status === tab);
  const readyCount = pitches.filter(pitch => pitch.status === 'ready').length;
  const staleCount = pitches.filter(pitch => pitch.isStale).length;

  if (!loading && organizations.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Pitches need an organization</Typography>
          <Button variant="contained" component={RouterLink} to="/organizations">
            Set up an organization
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <EntityCommentsProvider organizationId={organizationId}>
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/shape-up"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          color="inherit"
          underline="hover"
        >
          <LandscapeIcon fontSize="small" />
          Shape Up
        </Link>
        <Typography color="text.primary">Betting table</Typography>
      </Breadcrumbs>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" gutterBottom>Betting table</Typography>
            <Typography variant="body1" color="text.secondary">
              Shaped work waiting for a decision. Nothing here is committed, and nothing
              queues up: a pitch that waits too long goes stale and needs shaping again.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setDialogPitch(null); setDialogOpen(true); }}
          >
            Shape something
          </Button>
        </Box>

        {readyCount > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {readyCount} {readyCount === 1 ? 'pitch is' : 'pitches are'} on the table
            {staleCount > 0 && `, ${staleCount} of them stale`}.
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        {TABS.map(entry => (
          <Tab
            key={entry.value}
            value={entry.value}
            label={`${entry.label} (${pitches.filter(p => p.status === entry.value).length})`}
          />
        ))}
      </Tabs>

      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      ) : shown.length === 0 ? (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>{EMPTY_COPY[tab].title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 460, mx: 'auto' }}>
            {EMPTY_COPY[tab].body}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setDialogPitch(null); setDialogOpen(true); }}
          >
            Shape something
          </Button>
        </Paper>
      ) : (
        shown.map(pitch => (
          <PitchCard
            key={pitch.id}
            pitch={pitch}
            onEdit={(target) => { setDialogPitch(target); setDialogOpen(true); }}
            onDelete={handleDelete}
            onMarkReady={handleMarkReady}
            onBet={setBettingPitch}
          />
        ))
      )}

      <PitchDialog
        open={dialogOpen}
        pitch={dialogPitch}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />

      <ChooseCycleDialog
        open={Boolean(bettingPitch)}
        title="Bet on this pitch"
        description={bettingPitch
          ? `${bettingPitch.title} becomes a bet in the cycle you pick, carrying its appetite `
            + 'and guardrails. Link it to a Jira epic afterwards to fill the hill.'
          : ''}
        cycles={cycles}
        onClose={() => setBettingPitch(null)}
        onSubmit={handleBet}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
    </EntityCommentsProvider>
  );
};

export default PitchesPage;
